import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { isAfter } from 'date-fns';
import camelCase from 'lodash/camelCase';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import History from '../history';
import { getLocalStorage, setLocalStorage } from '../localStorageService';

const EXPIRE_MINUTES = 5;

interface IAuthState {
  token: string;
  expiresAt: string;
  renewToken: string;
  renewTokenExpiresAt: string;
  mfaToken: string;
  rememberMe: boolean;
  error: any;
}

type FetchToken<T = IAuthState> = (renewToken: string) => Promise<Partial<T>>;

const refreshAccessToken = async (fetchToken: FetchToken): Promise<string | null> => {
  const { token, rememberMe, expiresAt, renewToken } = getLocalStorage<IAuthState>('auth');
  const expiryDate = Date.parse(expiresAt);
  const isLoggedIn = !!token.length;
  const refreshDate = new Date().getTime() + EXPIRE_MINUTES * 60000;

  if (rememberMe && isLoggedIn && isAfter(refreshDate, expiryDate)) {
    const tokenData = await fetchToken(renewToken);

    setLocalStorage('auth', tokenData);

    return tokenData?.token;
  }
  return null;
};

export const addAuthHeader = (request: AxiosRequestConfig) => {
  const storage = getLocalStorage<IAuthState>('auth');
  const intercepted = request;
  if (storage?.token) {
    intercepted.headers.Authorization = `Bearer ${storage.token}`;
  }
  return intercepted;
};

export const refreshToken =
  <T = IAuthState>(instance: AxiosInstance, fetchToken: FetchToken<T>) =>
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (error?.response?.status === 403) {
      const newToken = await refreshAccessToken(fetchToken);

      if (!newToken) {
        History.push('/logout/');
      } else {
        return instance(originalRequest);
      }
    }

    return Promise.reject(error);
  };

export const logoutOnUnauthorized = async (error: AxiosError) => {
  const authStorage = getLocalStorage<IAuthState>('auth');
  const isLoggedIn = authStorage?.token?.length ?? false;

  if (error?.response?.status === 401 && isLoggedIn) {
    History.push('/logout/');
  }

  return Promise.reject(error);
};

const isObject = (value: unknown): boolean => !Array.isArray(value) && typeof value === 'object';

export const convertObjectCase = (
  object: Record<string, unknown>,
  converter: (word: string) => string
) =>
  Object.keys(object ?? {}).reduce<Record<string, unknown>>((converted, key) => {
    const result = { ...converted };
    let value: unknown = object[key];
    if (isObject(value) && !isNil(value)) {
      value = convertObjectCase(value as Record<string, unknown>, converter);
    }

    result[converter(key)] = value;
    return result;
  }, {});

export const convertRequestKeys = (request: AxiosRequestConfig) => {
  if (request?.data) {
    request.data = convertObjectCase(request.data, snakeCase);
  }
  return request;
};

export const convertResponseKeys = (response: AxiosResponse) => {
  if (Array.isArray(response.data)) {
    response.data = response.data.map((object) =>
      isObject(object) ? convertObjectCase(object, camelCase) : object
    );
  } else if (isObject(response.data)) {
    response.data = convertObjectCase(response.data, camelCase);
  }
  return response;
};
